import type { SubscriptionFeatureKey } from '@atsready/shared';
import { z } from 'zod';

export const SUBSCRIPTION_PLAN_TIERS = ['FREE', 'BASIC', 'PRO', 'PRO_PLUS'] as const;
export const STRIPE_SYNC_STATUSES = ['synced', 'pending', 'failed'] as const;

/**
 * The feature keys the entitlement editor shows, **in the order it shows them**
 * — `PlanManagement.tsx` maps straight over this array to build the rows, and
 * `entitlementFormSchema` below turns it into the `z.enum` that validates what
 * the admin submits. So the order is presentation and the membership is a
 * contract, and only one of the two is a copy.
 *
 * It is the estate's **third** hand-written copy of this list. The canonical one
 * is `FEATURE_KEYS` in `api-shared/src/lib/subscription-catalog.ts`, which is
 * what `seed-subscription-catalog.ts` writes into `plan_entitlements`; the
 * second is the `SubscriptionFeatureKey` union in `shared`. `admin` depends on
 * `@atsready/shared` and **not** on `@atsready/api-shared` — a browser app has
 * no business pulling in drizzle and `@libsql/client` — so the canonical array
 * is not importable here, and that is why this copy exists.
 *
 * It said *third* while a **fourth** sat in the same package: `services/types.ts`
 * spelled the twelve keys out again as a bare union with no guard at all. On
 * 2026-09-11 that became `type PlanFeatureKey = SubscriptionFeatureKey`, so the
 * census above is true again rather than one short. Do not re-spell it there.
 *
 * What was missing was any reason it should stay in step. Until 2026-08-29 all
 * three lists were independent literals with no cross-package test anywhere, and
 * nothing would have gone red if they drifted (disclosure D-20). The two clauses
 * below close that at compile time and cost no runtime:
 *
 * - `satisfies readonly SubscriptionFeatureKey[]` rejects a key that is not in
 *   the union — a typo, or one invented here.
 * - `PLAN_FEATURE_KEYS_ARE_EXHAUSTIVE` rejects a key that is in the union and
 *   missing from this array.
 *
 * What those two clauses do **not** reach is `api-shared`. Measured 2026-09-11:
 * `subscription-catalog.spec.ts` pins `FEATURE_KEYS` against a literal declared
 * inside the test and never imports `@atsready/shared`, so a thirteenth key added
 * to the canonical array produces zero compile errors here. The guards below tie
 * this array to `shared`'s union and nothing ties that union to `api-shared`;
 * closing the last link needs a cross-package assertion neither package has.
 */
export const PLAN_FEATURE_KEYS = [
  'resume_import',
  'resume_builder',
  // `export_pdf` and `export_docx` sit at 3-4 rather than at the end: the four
  // rows every plan carries are grouped at the top, so a reader meets what is
  // included everywhere before what distinguishes the tiers. The public price
  // list renders the same order from `FEATURE_KEYS`, and the two arrays are
  // byte-identical in order on purpose - see the ordered pin in
  // `__tests__/plan-management-schemas.test.ts`, which is the only thing in
  // this package that goes red if they drift, because both guards below are
  // membership-only and neither sees order.
  'export_pdf',
  'export_docx',
  'ai_builder',
  'ai_ats_score',
  'ai_job_matching',
  'ai_tailoring',
  'ai_suggestions',
  'chrome_extension',
  'job_imports',
  'priority_support',
] as const satisfies readonly SubscriptionFeatureKey[];

/** A key in `SubscriptionFeatureKey` that the array above forgot. Must be `never`. */
export type UnlistedPlanFeatureKey = Exclude<
  SubscriptionFeatureKey,
  (typeof PLAN_FEATURE_KEYS)[number]
>;

/**
 * `true` only while `UnlistedPlanFeatureKey` is `never`. Add a key to the
 * canonical list without adding it here and this assignment stops compiling,
 * naming the file that has to change.
 *
 * The tuple wrappers are load-bearing: a bare `UnlistedPlanFeatureKey extends
 * never` distributes over the union and yields `never` rather than `true`, so
 * the check would silently pass for every input.
 */
export const PLAN_FEATURE_KEYS_ARE_EXHAUSTIVE: [UnlistedPlanFeatureKey] extends [
  never,
]
  ? true
  : false = true;

export const planFormSchema = z.object({
  tier: z.enum(SUBSCRIPTION_PLAN_TIERS),
  name: z.string().min(1).max(80),
  description: z.string().min(1).max(280),
  monthlyPrice: z.coerce.number().int().min(0),
  annualDiscountPercent: z.coerce.number().int().min(0).max(100),
  sortOrder: z.coerce.number().int().min(0),
  isActive: z.boolean().default(true),
});

export type PlanFormInput = z.input<typeof planFormSchema>;
export type PlanFormOutput = z.output<typeof planFormSchema>;

export const entitlementFormSchema = z.object({
  entitlements: z.array(
    z.object({
      featureKey: z.enum(PLAN_FEATURE_KEYS),
      enabled: z.boolean(),
      limit: z.coerce.number().int().min(0).nullable(),
    }),
  ),
});

export type EntitlementFormInput = z.input<typeof entitlementFormSchema>;
export type EntitlementFormOutput = z.output<typeof entitlementFormSchema>;
